import React, {Component, ReactNode} from 'react'
import { Box, DialogContent, Typography } from '@mui/material';
import {RowFlexContainer} from '../../../../../styledComponents/FlexComponents'
import {NotifyDialogTitle} from '../../../../../components/assocDirector/AssocDirectorCommon'
import {StyledPre} from '../../../../../styledComponents/adminPage/StyledPre'
import {DialogCloseButton} from '../../../../../components/reservations/components/CommonStyle'
import {Close} from '@mui/icons-material'
import EventEmitterExAdmin from '../../../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {
    ApplyDialogActions,
    ApplyPrimaryButton,
    FormLabelContainer,
    OutlinedTextArea,
    SpApplyDialogTitle,
    SpApplyDialogTitleFont,
    SpDialogApplyProcessTag,
    StyledApplyFormLabel,
    StyledHeader,
    StyledHeadingHr,
    StyledPageFooterArea,
} from './NatAdminApplicationCommon'
import {PageFotterAreaRight} from '../../../../../components/shared/hoc/withAdminPageTemplate'
import {APPLY_IRREGULAR_PATTERN, RESIDENT_APPLY_PROCESS} from '../../../../../CodeMaster'
import {BoldSpan} from '../../../../../styledComponents/StyledSpan'
import {AdminDialog} from '../../../../../components/shared/pageBaseComponents/AdminDialog'
import {AppAdminContext} from '../../../../../app/AppAdminContext'
import {OptionalChip} from '../../../../../styledComponents/adminPage/Table'

interface Props {
    open: boolean
    optionComponent?: React.ReactNode
    approvalButtonDisabled?: boolean
    residentApplyProcess: RESIDENT_APPLY_PROCESS
    applyIrregularPattern?: APPLY_IRREGULAR_PATTERN
    onChange?: (value: string) => void
    onApproval: () => Promise<void>
    onCancel: () => void
    eventEmitter: EventEmitterExAdmin
}

interface State {
    comment: string
}

export default class NatAdminApplicationApprovalDialog extends Component<Props, State> {
    declare context: React.ContextType<typeof AppAdminContext>
    constructor(props: Readonly<Props>) {
        super(props)
        this.state = {
            comment: '',
        }

        this.handleMemoChange = this.handleMemoChange.bind(this)
    }

    componentDidUpdate(prevProps: Props): void {
        if (prevProps.open !== this.props.open) {
            this.setState({
                comment: '',
            })
        }
    }

    handleMemoChange(event: React.ChangeEvent<HTMLInputElement>): void {
        this.setState({comment: event.target.value}, () => {
            if (this.props.onChange) {
                this.props.onChange(this.state.comment)
            }
        })
    }

    renderDescription(residentApplyProcess: RESIDENT_APPLY_PROCESS,
                      applyIrregularPattern?: APPLY_IRREGULAR_PATTERN,
    ): ReactNode {
        switch (residentApplyProcess) {
            case RESIDENT_APPLY_PROCESS.ASSOC_DIRECTOR_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.ASSOC_DIRECTOR_DELEGATE_APPROVAL) {
                    return <>
                        <div>この申請を理事長の代わりに承認します。</div>
                        <div>申請者へのコメントは必要な場合のみご入力ください。</div>
                        <div>承認操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                    </>
                } else {
                    return <>
                        <div>この申請を承認します。</div>
                        <div>申請者へのコメントは必要な場合のみご入力ください。</div>
                        <div>承認操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                        <div>また、<BoldSpan>承認後は申請内容を確認できなくなりますのでご注意ください。</BoldSpan></div>
                    </>
                }
            case RESIDENT_APPLY_PROCESS.PET_CHAIRMAN_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.NO_PET_CLUB) {
                    return <>
                        <div>この申請を承認として処理します。</div>
                        <div>理事長への連絡事項は必要な場合のみご入力ください。</div>
                        <div>承認操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                    </>
                } else {
                    return <>
                        <div>この申請をペットクラブ委員長の代わりに承認します。</div>
                        <div>理事長への連絡事項は必要な場合のみご入力ください。</div>
                        <div>承認操作の取り消しはできません。申請内容に見落としがないか十分にご確認ください。</div>
                    </>
                }
            default:
                return null
        }
    }

    renderApplyProcessTag(
        residentApplyProcess: RESIDENT_APPLY_PROCESS,
        applyIrregularPattern?: APPLY_IRREGULAR_PATTERN,
    ): ReactNode {
        switch (residentApplyProcess) {
            case RESIDENT_APPLY_PROCESS.ASSOC_DIRECTOR_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.ASSOC_DIRECTOR_DELEGATE_APPROVAL) {
                    return (
                        <SpDialogApplyProcessTag>理事長代行</SpDialogApplyProcessTag>
                    )
                } else {
                    return null
                }
            case RESIDENT_APPLY_PROCESS.PET_CHAIRMAN_APPROVAL:
                if (applyIrregularPattern === APPLY_IRREGULAR_PATTERN.NO_PET_CLUB) {
                    return (
                        <SpDialogApplyProcessTag>ペットクラブなし</SpDialogApplyProcessTag>
                    )
                } else {
                    return (
                        <SpDialogApplyProcessTag>ペットクラブ委員長代行</SpDialogApplyProcessTag>
                    )
                }
            default:
                return null
        }
    }

    renderDialogContent(): ReactNode {
        const {
            residentApplyProcess,
            applyIrregularPattern,
        } = this.props
        const {comment} = this.state

        return (
            <>
                {this.renderApplyProcessTag(residentApplyProcess, applyIrregularPattern)}
                <Box mb={'2px'}>
                    <StyledPre>
                        {this.renderDescription(residentApplyProcess, applyIrregularPattern)}
                    </StyledPre>
                </Box>
                {this.props.optionComponent}
                {residentApplyProcess === RESIDENT_APPLY_PROCESS.PET_CHAIRMAN_APPROVAL ?
                    <Box mt={'16px'}>
                        <FormLabelContainer>
                            <StyledApplyFormLabel>
                                理事長への連絡事項
                            </StyledApplyFormLabel>
                            <Box ml={'8px'}><OptionalChip label={'任意'}/></Box>
                        </FormLabelContainer>
                        <OutlinedTextArea
                            InputProps={{
                                inputProps: {
                                    maxLength: 200,
                                    minRows: 6,
                                    placeholder: '200文字以内',
                                },
                            }}
                            fullWidth
                            multiline
                            value={comment}
                            eventKeys={['comment']}
                            eventEmitter={this.props.eventEmitter}
                            onChange={this.handleMemoChange}
                        />
                        <Box mt={'8px'} fontSize={'12px'}>
                            <StyledPre>{'・管理画面でのみ参照可能で、申請者には開示されません。' +
                                '\n・申請受付後は理事長への連絡事項を編集・削除することができません。ご注意ください。'}</StyledPre>
                        </Box>
                    </Box>
                    :
                    <Box mt={'16px'}>
                        <FormLabelContainer>
                            <StyledApplyFormLabel>
                                申請者へのコメント
                            </StyledApplyFormLabel>
                            {!this.props.optionComponent && <Box ml={'8px'}><OptionalChip label={'任意'}/></Box>}
                        </FormLabelContainer>
                        <OutlinedTextArea
                            InputProps={{
                                inputProps: {
                                    maxLength: 200,
                                    minRows: 6,
                                    placeholder: '200文字以内',
                                },
                            }}
                            fullWidth
                            multiline
                            value={comment}
                            eventKeys={['comment']}
                            eventEmitter={this.props.eventEmitter}
                            onChange={this.handleMemoChange}
                        />
                        <Box mt={'8px'} fontSize={'12px'}>
                            <StyledPre>・申請承認後はコメントを編集・削除することができません。ご注意ください。</StyledPre>
                        </Box>
                    </Box>
                }
            </>
        )
    }

    renderPc(): ReactNode {
        const {
            onApproval,
            onCancel,
        } = this.props

        return (
            <Box width={'750px'}>
                <NotifyDialogTitle>
                    <StyledHeader>
                        <RowFlexContainer>
                            <Typography variant="h5" display={'inline'}>
                                申請の承認
                            </Typography>
                            <DialogCloseButton onClick={onCancel} id={'close'}>
                                <Close/>
                            </DialogCloseButton>
                        </RowFlexContainer>
                    </StyledHeader>
                    <StyledHeadingHr/>
                </NotifyDialogTitle>
                <DialogContent>{this.renderDialogContent()}</DialogContent>
                <ApplyDialogActions>
                    <StyledPageFooterArea>
                        <PageFotterAreaRight>
                            <ApplyPrimaryButton
                                disabled={this.props.approvalButtonDisabled ?? false}
                                onClick={onApproval}
                            >
                                この申請を承認する
                            </ApplyPrimaryButton>
                        </PageFotterAreaRight>
                    </StyledPageFooterArea>
                </ApplyDialogActions>
            </Box>
        )
    }

    renderSp(): ReactNode {
        const {
            onApproval,
            onCancel,
        } = this.props

        return (
            <>
                <SpApplyDialogTitle>
                    <RowFlexContainer>
                        <SpApplyDialogTitleFont>申請の承認</SpApplyDialogTitleFont>
                        <DialogCloseButton onClick={onCancel}>
                            <Close fontSize={'large'}/>
                        </DialogCloseButton>
                    </RowFlexContainer>
                </SpApplyDialogTitle>
                <DialogContent>{this.renderDialogContent()}</DialogContent>
                <ApplyDialogActions>
                    <ApplyPrimaryButton
                        disabled={this.props.approvalButtonDisabled ?? false}
                        onClick={onApproval}
                        width={'100%'}
                    >
                        この申請を承認する
                    </ApplyPrimaryButton>
                </ApplyDialogActions>
            </>
        )
    }

    render(): ReactNode {
        const spMode = this.context.showSpModePage

        return (
            <AdminDialog maxWidth={'md'} open={this.props.open}>
                {spMode ? this.renderSp() : this.renderPc()}
            </AdminDialog>
        )
    }
}

NatAdminApplicationApprovalDialog.contextType = AppAdminContext
